import { Search } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import imagenHero from "../assets/economicos/abierto-negocio.jpg";
import { properties } from "@/data/properties";
import { locationCatalog } from "@/data/locationCatalog";
import { usePublishedProperties } from "@/hooks/usePublishedProperties";

const normalizeLocationQuery = (text) =>
  (text || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[-\s]+/g, " ")
    .trim();

const formatLocationSuggestion = (value = "") =>
  String(value || "")
    .replace(/^colonia\s*:?\s*/i, "")
    .replace(/^calle\s*:?\s*/i, "")
    .replace(/^blvd\s*:?\s*/i, "Blvd ")
    .replace(/^av\s*:?\s*/i, "Av ")
    .replace(/\s+/g, " ")
    .trim();

const formatPropertyLocationSuggestion = (value = "") =>
  formatLocationSuggestion(value)
    .split(",")[0]
    .replace(/\bfrente\s+a?.*$/i, "")
    .replace(/\ba\s+media\s+cuadra.*$/i, "")
    .replace(/\bcasi\s+esquina.*$/i, "")
    .replace(/\s+/g, " ")
    .trim();

const getLocalLocationSuggestions = (items = []) => {
  const suggestions = new Map();

  locationCatalog.forEach((value) => {
    const cleanValue = formatLocationSuggestion(value);
    const key = normalizeLocationQuery(cleanValue);
    if (cleanValue && !suggestions.has(key)) {
      suggestions.set(key, {
        place_id: `catalog-${key}`,
        description: cleanValue,
        isLocal: true,
      });
    }
  });

  items.forEach((property) => {
    const baseValues = [
      property.city,
      property.ciudad,
      property.municipio,
      property.alcaldia,
      property.state,
      property.entidad,
      property.address,
      property.zonaDetalle,
    ];
    const joined = [property.address, property.city, property.ciudad, property.alcaldia].join(" ");
    const normalizedJoined = normalizeLocationQuery(joined);

    if (normalizedJoined.includes("auriga") || normalizedJoined.includes("coyoacan")) {
      baseValues.push("Coyoac\u00e1n, Ciudad de M\u00e9xico", "Ciudad de M\u00e9xico", "CDMX");
    }

    baseValues
      .map(formatPropertyLocationSuggestion)
      .filter((value) => value && !/buen estado/i.test(value))
      .forEach((value) => {
        const key = normalizeLocationQuery(value);
        if (!suggestions.has(key)) {
          suggestions.set(key, {
            place_id: `local-${key}`,
            description: value,
            isLocal: true,
          });
        }
      });
  });

  return Array.from(suggestions.values());
};

export const Hero = () => {
  const navigate = useNavigate();
  const publishedProperties = usePublishedProperties();
  const contenedorRef = useRef(null);
  const [busquedaColonia, setBusquedaColonia] = useState("");
  const [mostrarLista, setMostrarLista] = useState(false);
  const [sugerenciasUbicacion, setSugerenciasUbicacion] = useState([]);
  const [buscandoUbicacion, setBuscandoUbicacion] = useState(false);
  const [precio, setPrecio] = useState("");
  const [precioPersonalizado, setPrecioPersonalizado] = useState("");
  const [categoria, setCategoria] = useState("");
  const localLocationSuggestions = useMemo(
    () => getLocalLocationSuggestions([...publishedProperties, ...properties]),
    [publishedProperties]
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (contenedorRef.current && !contenedorRef.current.contains(event.target)) {
        setMostrarLista(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const query = busquedaColonia.trim();
    if (query.length < 2) {
      setSugerenciasUbicacion([]);
      setMostrarLista(false);
      return;
    }

    const localMatches = localLocationSuggestions
      .filter((suggestion) =>
        normalizeLocationQuery(suggestion.description).includes(normalizeLocationQuery(query))
      )
      .slice(0, 8);

    setSugerenciasUbicacion(localMatches);
    setMostrarLista(true);
    setBuscandoUbicacion(false);
  }, [busquedaColonia, localLocationSuggestions]);

  const normalize = (text) =>
    (text || "")
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[-\s]+/g, "-")
      .trim();

  const priceMap = {
    "2000-8000": { min: 2000, max: 8000 },
    "8000-15000": { min: 8000, max: 15000 },
    "15000-30000": { min: 15000, max: 30000 },
    "30000-50000": { min: 30000, max: 50000 },
    "50000-100000": { min: 50000, max: 100000 },
  };

  const customPriceAmount = Number(String(precioPersonalizado).replace(/[^\d.]/g, ""));
  const hasCustomPrice =
    precio === "otro" && Number.isFinite(customPriceAmount) && customPriceAmount > 0;
  const isEconomicPrice =
    precio === "2000-8000" ||
    (hasCustomPrice && customPriceAmount >= 2000 && customPriceAmount <= 8000);
  const canChooseEconomicos = !precio || isEconomicPrice;
  const effectiveCategory =
    isEconomicPrice ? "economico" : categoria === "economico" ? "" : categoria;

  const handlePriceChange = (value) => {
    setPrecio(value);

    if (value === "2000-8000") {
      setCategoria("economico");
      return;
    }

    if (value === "otro") {
      setPrecioPersonalizado("");
    }

    if (categoria === "economico") {
      setCategoria("");
    }
  };

  const handleSearch = () => {
    const params = new URLSearchParams();

    if (busquedaColonia) {
      params.set("location", normalize(busquedaColonia));
    }

    if (precio && priceMap[precio]) {
      params.set("minPrice", priceMap[precio].min);
      params.set("maxPrice", priceMap[precio].max);
    }

    if (hasCustomPrice && isEconomicPrice) {
      navigate(`/economicos?${params.toString()}`);
      return;
    }

    if (hasCustomPrice) {
      params.set("minPrice", customPriceAmount);
      params.set("maxPrice", customPriceAmount);
      params.set("customPrice", "1");
      if (effectiveCategory) {
        params.set("type", effectiveCategory);
      }
      navigate(`/buscar?${params.toString()}`);
      return;
    }

    if (isEconomicPrice) {
      navigate(`/economicos?${params.toString()}`);
      return;
    }

    if (effectiveCategory === "economico") {
      alert("Los accesibles solo estan disponibles entre $2,000 y $8,000");
      return;
    }

    if (effectiveCategory) {
      params.set("type", effectiveCategory);
    }

    navigate(`/buscar?${params.toString()}`);
  };

  return (
    <section className="relative flex min-h-[85vh] items-center justify-center">
      <div className="absolute inset-0 z-0">
        <img src={imagenHero} className="h-full w-full object-cover" alt="" />
        <div className="absolute inset-0 bg-black/60" />
      </div>

      <div className="relative z-10 w-full px-4 text-center">
        <h1 className="mb-4 text-4xl font-bold text-white sm:text-5xl lg:text-6xl">
          La plataforma inteligente para encontrar espacios comerciales en M&eacute;xico
        </h1>

        <p className="mx-auto mb-10 max-w-2xl text-base text-white/90 sm:text-lg">
          Nuestros <span className="font-bold text-orange-400">agentes de IA</span> te ayudan a encontrar el espacio con <span className="font-bold text-orange-400">mayor potencial</span> para tu negocio y te explican por qu&eacute;.
        </p>

        <div className="mt-6 flex flex-col items-center justify-center gap-4 md:flex-row">
          <div ref={contenedorRef} className="relative w-72">
            <Search className="absolute left-3 top-3 h-5 w-5 text-gray-400" />

            <input
              value={busquedaColonia}
              onChange={(event) => {
                setBusquedaColonia(event.target.value);
                setMostrarLista(event.target.value.trim().length >= 2);
              }}
              onFocus={() =>
                setMostrarLista(
                  busquedaColonia.trim().length >= 2 && sugerenciasUbicacion.length > 0
                )
              }
              placeholder="Ciudad, colonia o zona"
              className="w-full rounded border p-3 pl-10"
              autoComplete="off"
              spellCheck={false}
            />

            {mostrarLista && busquedaColonia.trim().length >= 2 && (
              <div className="absolute z-50 max-h-56 w-full overflow-y-auto rounded border bg-white text-left shadow">
                {buscandoUbicacion ? (
                  <div className="p-2 text-gray-500">Buscando en Mexico...</div>
                ) : sugerenciasUbicacion.length > 0 ? (
                  sugerenciasUbicacion.map((sugerencia) => (
                    <button
                      type="button"
                      key={sugerencia.place_id}
                      onClick={() => {
                        setBusquedaColonia(sugerencia.description);
                        setMostrarLista(false);
                      }}
                      className="block w-full cursor-pointer p-2 text-left text-sm text-slate-800 hover:bg-gray-100"
                    >
                      {sugerencia.description}
                    </button>
                  ))
                ) : (
                  <div className="p-2 text-sm text-gray-400">
                    Escribe colonia, ciudad o municipio de Mexico
                  </div>
                )}
              </div>
            )}
          </div>

          {precio === "otro" ? (
            <div className="relative w-72">
            <input
              value={precioPersonalizado}
              onChange={(event) => setPrecioPersonalizado(event.target.value)}
              inputMode="numeric"
              placeholder="Escribe tu presupuesto"
              className="w-full rounded border p-3 pr-10"
              autoFocus
            />
              <button
                type="button"
                onClick={() => {
                  setPrecio("");
                  setPrecioPersonalizado("");
                }}
                aria-label="Volver a precios"
                className="absolute right-2 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full text-slate-500 hover:bg-slate-100"
              >
                X
              </button>
            </div>
          ) : (
            <select
              value={precio}
              onChange={(event) => handlePriceChange(event.target.value)}
              className="w-72 rounded border p-3"
            >
              <option value="">Precio</option>
              <option value="2000-8000">2,000 - 8,000</option>
              <option value="8000-15000">8,000 - 15,000</option>
              <option value="15000-30000">15,000 - 30,000</option>
              <option value="30000-50000">30,000 - 50,000</option>
              <option value="50000-100000">50,000 - 100,000</option>
              <option value="otro">Otro precio</option>
            </select>
          )}

          <select
            value={effectiveCategory}
            onChange={(event) => setCategoria(event.target.value)}
            disabled={isEconomicPrice}
            className="w-72 rounded border p-3"
          >
            <option value="">Tipo</option>
            {canChooseEconomicos && <option value="economico">Accesibles</option>}
            <option value="local">Locales</option>
            <option value="oficina">Oficinas / Consultorios</option>
            <option value="casa">Casas para negocio</option>
            <option value="bodega">Bodegas</option>
            <option value="terreno">Terrenos</option>
          </select>

          <button onClick={handleSearch} className="rounded bg-white px-6 py-3 text-black">
            Buscar
          </button>
        </div>
      </div>
    </section>
  );
};

export default Hero;
